import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Package, Truck, CheckCircle, XCircle, Clock, X, Loader } from 'lucide-react';
// import { useCart } from '../context/CartContext';
import { API_ENDPOINTS, fetchWithTimeout } from '../config/api';
import { useAuth } from '../context/AuthContext';

interface OrderStatus {
  status: string;
  date: string;
  time: string;
  description: string;
}

interface ApiOrder {
  order_id: string;
  user_id: string;
  total_amount: string;
  delivery_fee: string;
  created_at: string;
  updated_at: string | null;
  delivery_address: string;
  status: string;
  order_items: Array<{
    id: string;
    order_id: string;
    product_id: string;
    variation_id: string;
    quantity: string;
    size: string;
    sub_total: string;
    type: string;
    date_created: string;
    variation: {
      variant_id: string;
      product_id: string;
      variant_name: string;
      variant_purchasing_price: string;
      variant_refilling_price: string;
      variant_image: string;
      product_name: string;
    };
  }>;
  payment: {
    method: 'mpesa' | 'cash';
    status: string;
    phone_number: string;
    ref_id: string;
    date: string | null;
    amount: string;
  };
}

interface OrderDetailsModalProps {
  order: ApiOrder;
  onClose: () => void;
}

const PAYMENT_POLL_INTERVAL = 10000; // 10 seconds

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({ order, onClose }) => {
  const { user } = useAuth();
  // const [orderStatus, setOrderStatus] = useState<any>(null);
  // const [pollInterval, setPollInterval] = useState<NodeJS.Timeout | null>(null);
  // const [pollingError, setPollingError] = useState<string | null>(null);

  useEffect(() => {
    if (order.payment.status === 'pending' && user?.user_id) {
      const interval = setInterval(async () => {
        try {
          const formData = new URLSearchParams();
          formData.append('order_id', order.order_id);
          formData.append('user_id', user.user_id);

          const response = await fetchWithTimeout(API_ENDPOINTS.GET_ORDER_STATUS, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            mode: 'cors',
            body: formData
          });

          if (!response.ok) {
            throw new Error('Failed to fetch order status');
          }

          const data = await response.json();
          
          if (data.success) {
            setOrderStatus(data.data);
            if (data.data.payment.status !== 'pending') {
              clearInterval(interval);
              setPollInterval(null);
            }
          } else {
            throw new Error(data.message || 'Failed to fetch order status');
          }
        } catch (err) {
          console.error('Error polling order status:', err);
          setPollingError('Failed to update order status');
          clearInterval(interval);
          setPollInterval(null);
        }
      }, PAYMENT_POLL_INTERVAL);

      setPollInterval(interval);

      return () => {
        clearInterval(interval);
      };
    }
  }, [order.order_id, order.payment.status, user?.user_id]);

  const getOrderTimeline = (status: string): OrderStatus[] => {
    const timeline: OrderStatus[] = [
      {
        status: 'placed',
        date: order.created_at,
        time: '09:00 AM',
        description: 'Order placed successfully'
      }
    ];

    if (['processing', 'shipped', 'delivered'].includes(status)) {
      timeline.push({
        status: 'processing',
        date: order.created_at,
        time: '10:30 AM',
        description: 'Order confirmed and processing'
      });
    }

    if (['shipped', 'delivered'].includes(status)) {
      timeline.push({
        status: 'shipped',
        date: order.created_at,
        time: '02:00 PM',
        description: 'Order has been shipped'
      });
    }

    if (status === 'delivered') {
      timeline.push({
        status: 'delivered',
        date: order.created_at,
        time: '05:30 PM',
        description: 'Order delivered successfully'
      });
    }

    if (status === 'cancelled') {
      timeline.push({
        status: 'cancelled',
        date: order.created_at,
        time: '11:00 AM',
        description: 'Order was cancelled'
      });
    }

    return timeline;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'placed':
        return <Clock className="h-6 w-6 text-green-500" />;
      case 'processing':
        return <Package className="h-6 w-6 text-yellow-500" />;
      case 'shipped':
        return <Truck className="h-6 w-6 text-green-500" />;
      case 'delivered':
        return <CheckCircle className="h-6 w-6 text-green-500" />;
      case 'cancelled':
        return <XCircle className="h-6 w-6 text-red-500" />;
      default:
        return <Package className="h-6 w-6 text-gray-500" />;
    }
  };

  const timeline = getOrderTimeline(order.status);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            Order Details #{order.order_id}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Order Timeline */}
          <div className="mb-8">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Order Timeline
            </h4>
            <div className="space-y-6">
              {timeline.map((event, index) => (
                <div key={event.status} className="flex items-start">
                  <div className="flex-shrink-0">
                    {getStatusIcon(event.status)}
                  </div>
                  <div className="ml-4 flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white capitalize">
                          {event.status}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {event.description}
                        </p>
                      </div>
                      <div className="text-right text-sm text-gray-500 dark:text-gray-400">
                        <p>{event.date}</p>
                        <p>{event.time}</p>
                      </div>
                    </div>
                    {index < timeline.length - 1 && (
                      <div className="mt-2 ml-3 h-12 border-l-2 border-gray-200 dark:border-gray-700" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Items */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Order Items
            </h4>
            <div className="space-y-4">
              {order.order_items.map((item) => (
                <div 
                  key={item.id}
                  className="flex justify-between items-start pb-4 border-b border-gray-200 dark:border-gray-700"
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {item.variation.product_name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {item.size} - {item.type}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Quantity: {item.quantity}
                    </p>
                  </div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    KSH {item.sub_total}
                  </p>
                </div>
              ))}
              <div className="flex justify-between items-center pt-4">
                <span className="font-semibold text-gray-900 dark:text-white">Total</span>
                <span className="font-bold text-gray-900 dark:text-white">
                  KSH {order.total_amount}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Orders: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState<ApiOrder[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<ApiOrder | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const orderType = searchParams.get('type') || 'all';

  useEffect(() => {
    const fetchOrders = async () => {
      if (!user?.user_id) {
        setIsLoading(false);
        navigate('/login');
        return;
      }

      try {
        const formData = new URLSearchParams();
        formData.append('user_id', user.user_id);

        const response = await fetchWithTimeout(API_ENDPOINTS.GET_USER_ORDERS, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          mode: 'cors',
          body: formData
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.success) {
          setOrders(data.data || []);
        } else {
          setError(data.message || 'Failed to fetch orders');
        }
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('Failed to load orders. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, [user?.user_id, navigate]);

  const filteredOrders = orderType === 'all' 
    ? orders
    : orders.filter(order => order.status === orderType);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'processing':
        return <Package className="h-5 w-5 text-yellow-500" />;
      case 'shipped':
        return <Truck className="h-5 w-5 text-green-500" />;
      case 'delivered':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Package className="h-5 w-5 text-gray-500" />;
    }
  };

  return (
    <div className="lg:ml-64 min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 py-8 pt-24">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {orderType === 'all' ? 'All Orders' : `${orderType.charAt(0).toUpperCase() + orderType.slice(1)} Orders`}
          </h1>
        </div>
        
        <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="h-8 w-8 animate-spin text-green-600" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 dark:text-red-400">{error}</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400">No orders found</p>
            </div>
          ) : (
            filteredOrders.map((order) => (
              <div 
                key={order.order_id} 
                className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => setSelectedOrder(order)}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Order #{order.order_id}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Placed on {order.created_at}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(order.status)}
                    <span className="capitalize text-sm font-medium text-gray-900 dark:text-white">
                      {order.status}
                    </span>
                  </div>
                </div>
                
                {order.order_items.map((item) => (
                  <div key={item.id} className="py-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {item.variation.product_name}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {item.size} - {item.type}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Quantity: {item.quantity}
                        </p>
                      </div>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        KSH {item.sub_total}
                      </span>
                    </div>
                  </div>
                ))}
                
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-300">Payment Method</span>
                    <span className="capitalize text-gray-900 dark:text-white">
                      {order.payment.method}
                    </span>
                  </div>
                  <div className="flex justify-between mt-2">
                    <span className="font-semibold text-gray-900 dark:text-white">Total</span>
                    <span className="font-bold text-gray-900 dark:text-white">
                      KSH {order.total_amount}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Order Details Modal */}
        {selectedOrder && (
          <OrderDetailsModal
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
          />
        )}
      </div>
    </div>
  );
};